<script lang="ts" setup>
import {computed, ref} from "vue";

type TProps = {
    title: string
    handle: string
    values: string[]
    selected: Set<string>
    enabledFilters: {
        [key: string]: string[]
    }
}

const props = defineProps<TProps>()

const isShownMore = ref(true)

const shownProductsCount = 10

const availableFilters = computed(() => {
    return Object.values(props.values).filter((value) => !isDisabled(props.handle, value))
})

const shownFilters = computed(() => {
    // Helper function to parse inch measurements and metric thread sizes for sorting
    const parseInchMeasurement = (value: string): number => {
        // Remove quotes and trim
        const cleanValue = value.replace(/\"/g, '').trim()

        // Check if it's a metric thread size (e.g., "M 10 x 1" or "M 22 x 1,5")
        const metricMatch = cleanValue.match(/^M\s*(\d+(?:[.,]\d+)?)\s*x\s*(\d+(?:[.,]\d+)?)/i)
        if (metricMatch) {
            // Extract the main diameter and pitch
            const diameter = parseFloat(metricMatch[1].replace(',', '.'))
            // We primarily sort by diameter, so return it
            return diameter
        }

        // Check if it's a mixed number (e.g., "1 1/2")
        if (/^\d+\s+\d+\/\d+$/.test(cleanValue)) {
            const [whole, fraction] = cleanValue.split(/\s+/)
            const [numerator, denominator] = fraction.split('/')
            return parseInt(whole) + parseInt(numerator) / parseInt(denominator)
        }

        // Check if it's a simple fraction (e.g., "1/2")
        if (/^\d+\/\d+$/.test(cleanValue)) {
            const [numerator, denominator] = cleanValue.split('/')
            return parseInt(numerator) / parseInt(denominator)
        }

        // Try to parse as a regular number
        const num = parseFloat(cleanValue)
        return isNaN(num) ? 0 : num
    }

    // Helper function to get the first letter for sorting
    const getFirstLetter = (value: string): string => {
        // Clean the value by removing quotes and trimming
        const cleanValue = value.replace(/\"/g, '').trim()

        // Check if the value starts with a letter
        const match = cleanValue.match(/^[a-zA-Z]/)
        return match ? match[0].toUpperCase() : ''
    }

    // Separate items into two groups: with quotes and without quotes
    const withQuotes = availableFilters.value.filter(item => item.includes('\"'))
    const withoutQuotes = availableFilters.value.filter(item => !item.includes('\"'))

    // Sort each group
    withQuotes.sort((a, b) => {
        const letterA = getFirstLetter(a)
        const letterB = getFirstLetter(b)

        // If both have letters, sort by first letter
        if (letterA && letterB) {
            if (letterA !== letterB) {
                return letterA.localeCompare(letterB)
            }
        }
        // If one has a letter and the other doesn't, the one without comes first
        else if (letterA) return 1
        else if (letterB) return -1

        // For items without letters or with the same first letter, try to parse as inch measurements
        const numA = parseInchMeasurement(a)
        const numB = parseInchMeasurement(b)

        // If both are valid numbers, compare numerically
        if (!isNaN(numA) && !isNaN(numB)) {
            return numA - numB
        }

        // Fall back to standard string comparison
        return a.localeCompare(b)
    })

    withoutQuotes.sort((a, b) => {
        const letterA = getFirstLetter(a)
        const letterB = getFirstLetter(b)

        // If both have letters, sort by first letter
        if (letterA && letterB) {
            if (letterA !== letterB) {
                return letterA.localeCompare(letterB)
            }
        }
        // If one has a letter and the other doesn't, the one without comes first
        else if (letterA) return 1
        else if (letterB) return -1

        // For items without letters or with the same first letter, try to parse as inch measurements
        const numA = parseInchMeasurement(a)
        const numB = parseInchMeasurement(b)

        // If both are valid numbers, compare numerically
        if (!isNaN(numA) && !isNaN(numB)) {
            return numA - numB
        }

        // Fall back to standard string comparison
        return a.localeCompare(b)
    })

    // Combine the groups with the quotes group first
    const filters = [...withQuotes, ...withoutQuotes]

    if (!isShownMore.value) {
       //return filters.slice(0, 10)
    }

    return filters
})

const emit = defineEmits<{
    (e: 'change', options: { handle: string, value: string }): void
}>()

const isDisabled = (handle: string, value: string) => {
    if (typeof props.enabledFilters[handle] === 'undefined') {
        return true
    }

    return !props.enabledFilters[handle].includes(value)
}

function openFilter(filterId: string) {
    const element: HTMLElement | null = document.getElementById(filterId);

    if (element) {
        const currentClassList: DOMTokenList = element.classList;
  
        if (currentClassList.contains('show')) {
            currentClassList.remove('show');
            currentClassList.add('collapse');
        } else {
            currentClassList.add('show');
	    currentClassList.remove('collapse');
        }
    } else {
        console.error(`Element with ID '${filterId}' not found!`);
    }
}
</script>

<template>
    <template v-if="shownFilters.length">
	<details class="filter-accordion" style="margin-bottom: 25px">
	    <summary class="filter-accordion-title">
                {{ props.title }}
	    </summary>
	    <div class="filter-accordion-body">
	            <div class="accordion-body flex flex-col gap-2 text-left max-h-[320px] overflow-y-scroll" style="padding-top: 10px">
                        <template v-for="_value in shownFilters">
                            <label v-if="!isDisabled(props.handle, _value)" :class="{'opacity-50': isDisabled(props.handle, _value)}">
                                <input :checked="props.selected.has(_value)" :name="props.handle" :value="_value" type="checkbox" @change="emit('change', {handle: props.handle, value: _value})">
                                {{ _value }}
                            </label>
                        </template>
                    </div>
	    </div>
	</details>
    </template>
</template>

<style lang="scss">
    .filter-accordion {
  position: relative;
  display: block;
  max-width: 650px;
  margin: 0 auto 10px;
  background: rgba(255,255,255,0.5);
  border-radius: 5px;
  transition: 0.3s;
  overflow: hidden;
}

.filter-accordion::after {
  position: absolute;
  right: 24px;
  top: 6px;
  content: "";
  display: block;
  width: 8px;
  height: 8px;
  border-right: 3px solid #000000;
  border-bottom: 3px solid #000000;
  transform: rotate(-45deg);
  transform-origin: center;
  transition: .3s;
}

.filter-accordion[open]::after {
  transform: rotate(45deg);
}


.filter-accordion[open] .filter-accordion-title {
  margin-bottom: 0px;
}

.filter-accordion-title {
  background: #fff;
  border-radius: 5px;
  padding: 0px 0px;
  font-size: 15px;
  cursor: pointer;
  list-style: none;
  font-weight: 700;
  transition: .3s;
}

.filter-accordion-title::-webkit-details-marker {
  display: none;
}

.filter-accordion-body {
  padding: 0 0px;
  font-size: 18px;
}

.filter-accordion-body p:last-child {
  margin-bottom: 0;
}
.filter-accordion-body p:first-child {
  margin-top: 0;
}

</style>
